import { Box, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect } from "react";
import IntroImg from "../../../../images/introduction.png";
import codeImage from "../../../../images/CodeImage.png";
import createImage from "../../../../images/CreateImage.png";
import deployImage from "../../../../images/DeploymentImage.png";
import createhttps from "../../../../images/CreateHTTPSImage.png";
import deploytwo from "../../../../images/DeploymentImageTwo.png";
import createModal from "../../../../images/CreateModal.png";
import compErr from "../../../../images/CompErrImage.png";
import ContentBar from "./ContentBar";
import { useDocsStore } from "../../../../store/DocsStore";
import { useNavigate } from "react-router-dom";

const Documentation = () => {
  const { setActiveSelection } = useDocsStore();
  const smallerThan900 = useMediaQuery("(min-width:900px");
  const smallerThan600 = useMediaQuery("(min-width:600px");

  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setActiveSelection(sectionId);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSelection(entry.target.id); // Update the store based on the visible section
          }
        });
      },
      { threshold: 0.5 }
    );

    const sections = document.querySelectorAll(".section");
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [setActiveSelection]);

  return (
    <Box
      width={smallerThan900 ? "calc(1064px - 17px)" : "calc(90vw - 17px)"}
      display={"flex"}
      gap={6}
    >
      <Box
        display={"flex"}
        gap={3}
        flexDirection={"column"}
        marginBottom={"350px"}
      >
        <Typography variant="title">Documentation</Typography>

        <Typography id="section1" className="section" variant="subtitle">
          Getting Started
        </Typography>

        <Typography>
          Log into the{" "}
          <span
            className="span-link"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/login")}
          >
            console
          </span>{" "}
          with your UserID and Password. If you do not have access to an
          account, you can request a free trial or subscribe{" "}
          <span
            className="span-link"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/subscribe")}
          >
            here
          </span>
          .
        </Typography>

        <Typography marginTop={2}>
          Once logged in, you can click on{" "}
          <span className="span-bold">Getting started</span>.
        </Typography>

        {/* SCREENSHOT 1 */}

        <img className="doc-images" src={IntroImg} alt="Introduction" />
        <Typography
          id="section2"
          className="section"
          variant="subtitle"
          sx={{ marginTop: "24px" }}
        >
          Deploying HTTP Functions
        </Typography>
        <Typography>
          1. Once on the Create tab, select your programming language. You can
          choose between Go and Python. The code will auto fill with a basic
          example in the language selected. For example, selecting go will
          generate the following source code.
        </Typography>

        {/* SCREENSHOT 2 */}
        <img src={codeImage} alt={"Go Code"} width={975} height={501} />

        <Typography>
          2. Set the path URI and protocol to run the function and click Deploy.
          The URI is set to forward slash (/) by default and the protocol is set
          to HTTP since we are deploying HTTP functions. (**The URI field in the
          screenshot below should contain “/“ by default **)
        </Typography>

        {/* SCREENSHOT 3 */}
        <img
          src={createImage}
          alt={"Create Function"}
          width={975}
          height={1116}
        />

        <Typography>
          3. You will see the deployed function in your Deployments tab under
          Functions.
        </Typography>

        {/* SCREENSHOT 4 */}
        <img
          src={deployImage}
          alt={"Deploy Function"}
          width={975}
          height={694}
          loading="lazy"
        />

        <Typography>
          4. Open a Browser and copy/paste the entry point of your created
          function in the address field. You will see the output of the function
          <span className="span-bold"> Hello World !!</span>
        </Typography>

        {/* NEEDS A BIG MARGIN HERE */}
        <Typography
          id="section3"
          className="section"
          variant="subtitle"
          sx={{ marginTop: "24px" }}
        >
          Deploying HTTPS Functions
        </Typography>

        <Typography>
          The process for deploying HTTPS functions is very similar to deploying
          HTTP functions. Please review deploying HTTP functions before
          proceeding with HTTPS functions. We will deploy the same function,
          only we set the protocol to <span className="span-bold">HTTPS</span>{" "}
          along with a valid domain name.
        </Typography>

        <Typography>
          <span className="span-bold">Important:</span> The domain name must
          resolve to the entry point of your function. Otherwise, the function
          will not deploy successfully. You can manage DNS records through a
          public domain registry and run nslookup on your local machine to
          verify the changes. As an example, we use the domain{" "}
          {`(juicebox.publicvm.com)`}, set the A record to the entry point
          assigned to the function, and click{" "}
          <span className="span-bold">Deploy</span>.
        </Typography>

        {/* SCREENSHOT 5 */}
        <img
          src={createhttps}
          alt={"Create with HTTPS"}
          width={975}
          height={1118}
          loading="lazy"
        />

        <Typography>
          You will see your deployed HTTPS function in the Functions table,
          along with the certificate generated for SSL/TLS encryption under the
          Certificates table.
        </Typography>

        <Typography id="section4" className="section">
          <span className="bold">
            Note: Your new certificate can be assigned to many functions. Simply
            include the same certificate in the hostname field when creating
            your HTTPS functions. You can also create multiple certificates
            assign them to multiple functions for SNI{" "}
            {`(Server Name Indication)`} use cases.
          </span>
        </Typography>
        {/* SCREENSHOT 6 */}
        <img
          src={deploytwo}
          alt={"Deploy Screen"}
          width={975}
          height={694}
          loading="lazy"
        />
        <Typography>
          To test your function, enter your domain name in the browser. You
          should see the output of the function Hello World !! with a secure TLS
          connection.
        </Typography>

        {/* MARGIN HERE */}

        <Typography
          id="section5"
          className="section"
          variant="subtitle"
          sx={{ marginTop: "24px" }}
        >
          Applying Updates
        </Typography>

        <Typography>
          You can dynamically change functions on the fly with zero downtime. In
          the Deployments tab, select the edit icon next to the function you
          want to edit. You can modify the source code and name of the selected
          function and apply the changes. For example, changing the return
          message to Hello World !! This function was just updated !!
        </Typography>

        {/* SCREENSHOT 7 */}
        <img
          src={createModal}
          alt={"Create Modal"}
          width={975}
          height={1005}
          loading="lazy"
        />

        <Typography>
          Refresh your browser and reconnect to the modified function. You
          should see the new output.
        </Typography>

        {/* MARGIN HERE */}

        <Typography
          id="section6"
          className="section"
          variant="subtitle"
          sx={{ marginTop: "24px" }}
        >
          Compilation Errors
        </Typography>
        <Typography>
          Rawdat.AI will return error messages if functions fail to compile. For
          example, a syntax error in the source code will result in a failure
          with information pertaining to the error in question.
        </Typography>

        {/* SCREENSHOT 8 */}

        <img
          src={compErr}
          alt={"Go Code"}
          width={975}
          height={694}
          loading="lazy"
        />
        {/* MARGIN HERE */}

        <Typography
          id="section7"
          className="section"
          variant="subtitle"
          sx={{ marginTop: "24px" }}
        >
          Deleting Function
        </Typography>
        <Typography>
          You can delete functions by clicking on the DELETE icon next to the
          function you wish to delete. Once the function is deleted, refresh
          your browser, and reconnect to the deleted function. The browser
          should return a 404 response.
        </Typography>
      </Box>

      {smallerThan600 && (
        <Box>
          <ContentBar scrollToSection={scrollToSection} />
        </Box>
      )}
    </Box>
  );
};

export default Documentation;
